/*
Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained

access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.
*/

import { CircularProgress } from '@mui/material';
import { useMemo } from 'react';
import useLoanSubsidy from '../../common/hooks/useLoanSubsidy';
import { useAuthContext } from '../../contexts/AuthContext';
import { useLoanContext } from '../../contexts/LoanContext';

import * as constants from '../../common/constants';
import useQidListFullResponse from '../../common/hooks/useQidListFullResponse';
import Styles from '../../css/IRPITable.module.css';
import EnvelopeWithBox from '../EnvelopeWithBox';
import EditSubsidyForm from './EditSubsidyForm';
import AddSubsidyForm from './AddSubsidyForm';

function LoanSubsidyTable() {
  const { token, fullAccess } = useAuthContext();
  const { loanSubsidy, createLoanSubsidy, updateLoanSubsidy, loadingLoanSubsidy } = useLoanSubsidy();
  const {
    data: subsidyNameQidList,
    status: subsidyNameStatus,
    error: subsidyNameError,
  } = useQidListFullResponse(token, constants.subsidyNameQid);
  let newSubsidy: SubsidyObj = {
    loan: '',
    cnt: '',
    subsidyName: '',
    subsidyType: '',
    monthlyAmount: 0.0,
    startDate: null,
    endDate: null,
    terminationDate: null,
    agencySubsidyBalance: 0.0,
    agencySubsidyPaidToDate: 0.0,
    YTD: null,
    approvedAmount: 0.0,
    agencyCureAmount: 0.0,
    totalAgencySubsidyAmount: 0.0,
  };

  const subsidies = useMemo(() => {
    if (!Array.isArray(loanSubsidy)) return [];
    return loanSubsidy.sort((a, b) => a.startDate - b.startDate);
  }, [loanSubsidy]);

  return (
    <EnvelopeWithBox blockName='Loan Subsidies' className={Styles.envelope}>
      <table className={Styles.paymentTable}>
        <thead>
          <tr>
            <th scope='col'>Subsidy:</th>
            <th scope='col'>Monthly Amount:</th>
            <th scope='col'>Start Date:</th>
            <th scope='col'>Last Date:</th>
            <th scope='col'>Ended Date:</th>
            <th scope='col'>Balance:</th>
            <th scope='col'>PTD:</th>
            <th scope='col'>YTD:</th>
            <th scope='col'>Approved:</th>
            <th scope='col'>Cure Delinquency:</th>
            <th scope='col'>Total:</th>
          </tr>
        </thead>
        <tbody>
          {loadingLoanSubsidy ? (
            <tr>
              <td colSpan={4}>
                <CircularProgress />
              </td>
            </tr>
          ) : !subsidies || subsidies.length < 1 ? (
            <tr>
              <td colSpan={4}>No Data</td>
            </tr>
          ) : (
            subsidies.map((subsidy) => (
              <EditSubsidyForm {...subsidy} updateLoanSubsidy={updateLoanSubsidy} key={subsidy.cnt} />
            ))
          )}
          {fullAccess && <tr>
            <AddSubsidyForm
              {...newSubsidy}
              subsidyNameQidList={subsidyNameQidList}
              createLoanSubsidy={createLoanSubsidy}
            />
          </tr>}
        </tbody>
      </table>
    </EnvelopeWithBox>
  );
}

export default LoanSubsidyTable;
